import { defineStore } from 'pinia'

import { getAuthenticatedUser, loginWithPassword, type AuthUser } from './api'
import { clearStoredAuth, readStoredAuth, updateAccessToken, writeStoredAuth } from './session'
import { env } from '@/shared/config/env'

const profileStorageKey = `${env.authStorageKey}.profile`
let profileRequest: Promise<AuthUser> | null = null

function readProfile(): AuthUser | null {
  const raw =
    window.localStorage.getItem(profileStorageKey) ?? window.sessionStorage.getItem(profileStorageKey) ?? window.localStorage.getItem('auth_remember')
  if (!raw) return null
  try {
    const profile = JSON.parse(raw) as AuthUser
    window.localStorage.removeItem('auth_remember')
    return profile
  } catch {
    window.localStorage.removeItem(profileStorageKey)
    window.sessionStorage.removeItem(profileStorageKey)
    window.localStorage.removeItem('auth_remember')
    return null
  }
}

function writeProfile(profile: AuthUser | null) {
  window.localStorage.removeItem(profileStorageKey)
  window.sessionStorage.removeItem(profileStorageKey)
  if (!profile) return
  const target = readStoredAuth()?.remember === false ? window.sessionStorage : window.localStorage
  target.setItem(profileStorageKey, JSON.stringify(profile))
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    profile: null as AuthUser | null,
    accessToken: null as string | null,
    ready: false,
    profileSynchronized: false,
  }),
  actions: {
    boot() {
      if (this.ready) return
      const stored = readStoredAuth()
      this.accessToken = stored?.accessToken ?? null
      this.profile = readProfile()
      this.ready = true
    },
    setUser(user: AuthUser | null) {
      this.profile = user
      writeProfile(user)
    },
    setToken(accessToken: string, refreshToken?: string, remember = true) {
      const sessionChanged = this.accessToken !== accessToken
      const stored = readStoredAuth()
      writeStoredAuth({
        accessToken,
        ...((refreshToken ?? stored?.refreshToken) ? { refreshToken: refreshToken ?? stored?.refreshToken } : {}),
        remember,
      })
      this.accessToken = accessToken
      if (sessionChanged) this.profileSynchronized = false
    },
    async login(email: string, password: string, remember = true) {
      const tokens = await loginWithPassword(email, password)
      this.setToken(tokens.token, tokens.refresh, remember)
      return tokens
    },
    async fetchProfile() {
      if (profileRequest) return profileRequest

      const accessToken = this.accessToken
      profileRequest = getAuthenticatedUser()
        .then((profile) => {
          if (!accessToken || this.accessToken !== accessToken) throw new Error('Authentication session changed while loading the profile.')
          this.setUser(profile)
          this.profileSynchronized = true
          return profile
        })
        .finally(() => {
          profileRequest = null
        })

      return profileRequest
    },
    refreshFromStorage() {
      const stored = readStoredAuth()
      const sessionChanged = this.accessToken !== (stored?.accessToken ?? null)
      this.accessToken = stored?.accessToken ?? null
      if (sessionChanged) this.profileSynchronized = false
      if (stored?.accessToken) updateAccessToken(stored.accessToken)
    },
    logout() {
      clearStoredAuth()
      this.setUser(null)
      this.accessToken = null
      this.profileSynchronized = false
    },
  },
})
